import { useState, useEffect, useCallback } from 'react';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { WATERMARK_API_URL } from '../config';
import awsConfig from '../aws-config';

const APPLY_STORE_ASSET_WATERMARK = gql`
  mutation ApplyStoreAssetWatermark($input: ApplyStoreAssetWatermarkInput!) {
    applyStoreAssetWatermark(input: $input) {
      assetType
      watermarkedUrl
      configVersion
    }
  }
`;

const VALID_ASSET_TYPES = new Set(['cover', 'preview', 'thumbnail']);

function parseS3Uri(uri) {
  if (!uri || !uri.startsWith('s3://')) return null;
  const m = uri.match(/^s3:\/\/([^/]+)\/(.+)$/);
  return m ? { bucket: m[1], key: m[2] } : null;
}

/**
 * Loads the current distributor default watermark profile and exposes an
 * `applyAsset` helper that returns the watermarked public S3 URI when the
 * user's `applyTo.<assetType>` flag is true, or the original URI otherwise.
 */
export function useStoreWatermarkApply(token) {
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [applyMutation, { loading: isApplying }] = useMutation(APPLY_STORE_ASSET_WATERMARK);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) return;
      setConfigLoading(true);
      setConfigError(null);
      try {
        const res = await fetch(`${WATERMARK_API_URL}/v1/distributor/default-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to load watermark config: ${res.status}`);
        const data = await res.json();
        if (!cancelled) setConfig(data || null);
      } catch (err) {
        if (!cancelled) setConfigError(err.message);
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  const applyAsset = useCallback(async ({ originalUrl, assetType, collectionId }) => {
    const publicBucket = awsConfig.s3Buckets?.public;
    const loc = parseS3Uri(originalUrl);
    if (!loc || loc.bucket !== publicBucket || !loc.key.startsWith('store/')) {
      // Existing-media URIs (uploads bucket) or non-S3 URLs are left as-is.
      return originalUrl;
    }
    if (!VALID_ASSET_TYPES.has(assetType)) {
      return originalUrl;
    }

    const shouldApply = config?.applyTo?.[assetType] ?? false;
    if (!shouldApply) {
      return originalUrl;
    }

    try {
      const { data } = await applyMutation({
        variables: {
          input: {
            collectionId,
            sourceKey: originalUrl,
            assetType,
          },
        },
      });
      const watermarkedUrl = data?.applyStoreAssetWatermark?.watermarkedUrl;
      if (watermarkedUrl) {
        return watermarkedUrl;
      }
      return originalUrl;
    } catch (err) {
      // Surface a generic toast; keep the original upload visible and usable.
      console.warn('Watermark apply failed; keeping original asset', err);
      toast.warn('Watermark apply failed. Showing original asset.');
      return originalUrl;
    }
  }, [applyMutation, config]);

  return {
    config,
    configLoading,
    configError,
    isApplying,
    applyAsset,
  };
}
